import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Users, CalendarClock, CheckCircle2, DollarSign, ArrowRight } from "lucide-react";

export default function CoachDashboard() {
  const [stats, setStats] = useState(null);
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/lessons", { params: { status: "scheduled" } }).then(({ data }) => setLessons((data || []).slice(0, 6))).catch(() => {});
  }, []);

  const tiles = [
    { label: "Students", value: stats?.total_students ?? "—", icon: Users, test: "stat-students" },
    { label: "Upcoming", value: stats?.upcoming_lessons ?? "—", icon: CalendarClock, test: "stat-upcoming" },
    { label: "Completed", value: stats?.completed_lessons ?? "—", icon: CheckCircle2, test: "stat-completed" },
    { label: "Revenue", value: stats ? `$${Number(stats.revenue || 0).toFixed(0)}` : "—", icon: DollarSign, test: "stat-revenue" },
  ];

  return (
    <div data-testid="coach-dashboard" className="space-y-10">
      <section>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {tiles.map((t, i) => {
            const I = t.icon;
            return (
              <div key={t.label} className="card-surface p-6" data-testid={t.test}>
                <I size={18} className="text-[#38BDF8]" aria-hidden="true" />
                <div className="font-display text-5xl font-black mt-4">{t.value}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Schedule</div>
            <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">Upcoming Sessions</h2>
          </div>
          <Link to="/coach/calendar" className="text-xs uppercase tracking-widest font-bold text-[#38BDF8] hover:underline" data-testid="view-calendar-link">
            View calendar →
          </Link>
        </div>

        {lessons.length === 0 ? (
          <EmptyState text="No upcoming lessons. Create one on the calendar." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {lessons.map((l) => (
              <Link
                key={l.id}
                to={`/coach/lessons/${l.id}`}
                className="card-surface p-6 block group"
                data-testid={`upcoming-lesson-${l.id}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">
                  {new Date(l.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="font-display text-2xl font-black uppercase tracking-tight mt-2">{l.title}</div>
                <div className="text-xs text-[#A3A3A3] mt-3">
                  {l.type === "clinic" ? `Clinic · ${(l.booked_students || []).length}/${l.max_students}` : "Private lesson"}
                  {l.location ? ` · ${l.location}` : ""}
                </div>
                <div className="flex items-center text-xs mt-6 text-white group-hover:text-[#38BDF8] transition-colors">
                  Open <ArrowRight size={14} className="ml-2" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="card-surface p-8 relative overflow-hidden">
          <div className="absolute right-6 top-6 text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">AI Assist</div>
          <div className="font-display text-4xl font-black uppercase tracking-tighter">
            Let AI draft<br />your next plan.
          </div>
          <p className="text-sm text-[#A3A3A3] mt-4 max-w-md">
            Pick a student, set a focus, and our AI will generate a personalized warm-up, drills, scenarios, and homework in seconds.
          </p>
          <Link to="/coach/plans" className="btn-primary mt-6 px-6 py-3 text-xs inline-block" data-testid="ai-draft-cta">
            Draft a plan
          </Link>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-white/10 p-12 text-center text-[#A3A3A3]">
      {text}
    </div>
  );
}
